import React, { useState, useEffect } from 'react';
import { TrendingUp, ArrowUp, ArrowDown, Filter, Heart } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import Skeleton from '../components/ui/Skeleton';
import { useTrends } from '../hooks/useTrends';
import { useLocalStorage } from '../hooks/useLocalStorage';

const Trends = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'favorites'
  
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || 'all',
    region: searchParams.get('region') || 'all',
    source: searchParams.get('source') || 'all',
    search: searchParams.get('search') || '',
  });

  const [favorites, setFavorites] = useLocalStorage('trendy_favorites', []);
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);
  const [selectedTrend, setSelectedTrend] = useState(null);

  const { trends, loading } = useTrends(filters);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    const category = searchParams.get('category');
    const region = searchParams.get('region');
    const source = searchParams.get('source');
    const search = searchParams.get('search');
    
    setFilters({
      category: category || 'all',
      region: region || 'all',
      source: source || 'all',
      search: search || '',
    });
  }, [searchParams]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setSearchParams({ ...Object.fromEntries(searchParams), [key]: value });
  };

  const handleAddFavorite = (trend) => {
    setSelectedTrend(trend);
    setShowFavoriteModal(true);
  };

  const confirmAddFavorite = () => {
    if (selectedTrend && !favorites.find(fav => fav.id === selectedTrend.id)) {
      setFavorites([...favorites, selectedTrend]);
    } else if (selectedTrend && isFavorite(selectedTrend.id)) {
      setFavorites(favorites.filter(fav => fav.id !== selectedTrend.id));
    }
    setShowFavoriteModal(false);
  };

  const isFavorite = (trendId) => favorites.some(fav => fav.id === trendId);

  const getSentimentColor = (sentiment) => {
    if (sentiment > 0.3) return 'text-reddit-green';
    if (sentiment < -0.3) return 'text-reddit-red';
    return 'text-reddit-600 dark:text-dark-textMuted';
  };

  const removeFavorite = (trendId) => {
    setFavorites(favorites.filter(fav => fav.id !== trendId));
  };

  // Display favorites or all trends based on viewMode and filter by search
  let displayTrends = viewMode === 'favorites' ? favorites : trends;
  
  // Apply search filter if search query exists
  if (filters.search && filters.search.trim() !== '') {
    const searchLower = filters.search.toLowerCase();
    displayTrends = displayTrends.filter(trend => 
      trend.topic.toLowerCase().includes(searchLower) ||
      trend.category.toLowerCase().includes(searchLower) ||
      trend.source.toLowerCase().includes(searchLower)
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Trending Topics</h1>
          <p className="text-reddit-600 dark:text-dark-textMuted">
            {filters.search 
              ? `Search results for "${filters.search}"` 
              : 'Discover what\'s trending across the web in real-time'
            }
          </p>
        </div>
        {filters.search && (
          <Button 
            variant="outline" 
            onClick={() => {
              setFilters(prev => ({ ...prev, search: '' }));
              setSearchParams({ ...Object.fromEntries(searchParams), search: '' });
              window.history.pushState({}, '', '/trends');
            }}
          >
            Clear Search
          </Button>
        )}
      </div>

      {/* View Mode Toggle */}
      <div className="flex gap-2">
        <Button
          variant={viewMode === 'all' ? 'primary' : 'outline'}
          onClick={() => setViewMode('all')}
          className="flex-1 md:flex-none"
        >
          <TrendingUp className="w-4 h-4" />
          All Trends
        </Button>
        <Button
          variant={viewMode === 'favorites' ? 'primary' : 'outline'}
          onClick={() => setViewMode('favorites')}
          className="flex-1 md:flex-none"
        >
          <Heart className="w-4 h-4" />
          Favorites ({favorites.length})

        </Button>
      </div>
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-reddit-orange" />
            <h3 className="font-semibold">Filters</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <Select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="all">All Categories</option>
                <option value="technology">Technology</option>
                <option value="politics">Politics</option>
                <option value="sports">Sports</option>
                <option value="entertainment">Entertainment</option>
                <option value="business">Business</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Region</label>
              <Select
                value={filters.region}
                onChange={(e) => handleFilterChange('region', e.target.value)}
              >
                <option value="all">All Regions</option>
                <option value="global">Global</option>
                <option value="north america">North America</option>
                <option value="europe">Europe</option>
                <option value="asia">Asia</option>
                <option value="south america">South America</option>
                <option value="africa">Africa</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Source</label>
              <Select
                value={filters.source}
                onChange={(e) => handleFilterChange('source', e.target.value)}
              >
                <option value="all">All Sources</option>
                <option value="twitter">Twitter</option>
                <option value="reddit">Reddit</option>
                <option value="google trends">Google Trends</option>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trends Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {loading && viewMode === 'all' ? (
          <>
            {[...Array(8)].map((_, i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <Skeleton className="h-6 w-3/4 mb-3" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <>
            {displayTrends.map((trend) => (
              <Card key={trend.id} className="hover:scale-[1.02] transition-transform cursor-pointer">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                        {trend.topic}
                        {Math.abs(trend.growth) > 50 && (
                          <TrendingUp className="w-5 h-5 text-reddit-orange animate-pulse" />
                        )}
                      </h3>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge variant="blue">{trend.category}</Badge>
                        <Badge variant="green">{trend.region}</Badge>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-reddit-600 dark:text-dark-textMuted">Mentions</p>
                      <p className="text-2xl font-bold">{trend.mentions.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-reddit-600 dark:text-dark-textMuted">Growth</p>
                      <p className={`text-2xl font-bold flex items-center gap-1 ${
                        trend.growth > 0 ? 'text-reddit-green' : 'text-reddit-red'
                      }`}>
                        {trend.growth > 0 ? (
                          <ArrowUp className="w-5 h-5" />
                        ) : (
                          <ArrowDown className="w-5 h-5" />
                        )}
                        {Math.abs(trend.growth)}%
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm mb-4">
                    <div>
                      <span className="text-reddit-600 dark:text-dark-textMuted">Sentiment: </span>
                      <span className={`font-semibold ${getSentimentColor(trend.sentiment)}`}>
                        {trend.sentiment > 0 ? 'Positive' : trend.sentiment < 0 ? 'Negative' : 'Neutral'}
                      </span>
                    </div>
                    <div className="text-reddit-600 dark:text-dark-textMuted">
                      Source: <span className="font-medium">{trend.source}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {viewMode === 'favorites' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeFavorite(trend.id)}
                        className="text-reddit-red border-reddit-red hover:bg-reddit-red hover:text-white flex-1"
                      >
                        Remove from Favorites
                      </Button>
                    ) : (
                      <Button
                        variant={isFavorite(trend.id) ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() => handleAddFavorite(trend)}
                        className="flex-1"
                      >
                        <Heart className={`w-4 h-4 ${isFavorite(trend.id) ? 'fill-current' : ''}`} />
                        {isFavorite(trend.id) ? 'Favorited' : 'Add to Favorites'}
                      </Button>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-reddit-200 dark:border-dark-border">
                    <p className="text-xs text-reddit-600 dark:text-dark-textMuted">
                      Last updated: {new Date(trend.timestamp).toLocaleString()}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </div>

      {displayTrends.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Heart className="w-12 h-12 text-reddit-400 dark:text-dark-textMuted mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {viewMode === 'favorites' ? 'No favorites yet' : 'No trends found'}
            </h3>
            <p className="text-reddit-600 dark:text-dark-textMuted">
              {viewMode === 'favorites' 
                ? 'Add trends to your favorites by clicking the heart icon'
                : 'Try adjusting your filters to see more results'
              }
            </p>
          </CardContent>
        </Card>
      )}

      <Modal
        isOpen={showFavoriteModal}
        onClose={() => setShowFavoriteModal(false)}
        title="Add to Favorites"
        onConfirm={confirmAddFavorite}
        confirmText="Add Favorite"
      >
        {selectedTrend && (
          <div className="space-y-3">
            <p className="text-reddit-600 dark:text-dark-textMuted">
              Add "<strong>{selectedTrend.topic}</strong>" to your favorite trends?
            </p>
            <div className="bg-reddit-50 dark:bg-dark-border p-3 rounded-lg">
              <p className="text-sm">
                <span className="font-medium">Mentions:</span> {selectedTrend.mentions.toLocaleString()}
              </p>
              <p className="text-sm">
                <span className="font-medium">Category:</span> {selectedTrend.category}
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Trends;